import React, { useEffect, useState } from 'react';
import { Sizes } from '../../constants/Image';
import ImagePlaceholder from './ImagePlaceholder';

export type ImageData = {
  id?: number;
  urls: { [key: string]: string };
};

interface Props {
  className?: string;
  defaultSize: Sizes;
  image: ImageData;
  allSizes?: Sizes[];
  sizes?: string[];
  width?: string;
  height?: string;
  alt?: string;
}

const generateSrcSet = (image: ImageData, allSizes: Sizes[]) => {
  let srcSetImages: string[] = [];

  if (image?.urls) {
    srcSetImages = allSizes
      .map((size) => {
        if (image.urls['W' + size]) {
          return `${image.urls['W' + size]} ${size}w`;
        }
        return null;
      })
      .filter((size): size is string => size !== null);
  }

  return srcSetImages;
};

const Image: React.FC<Props> = ({
  image,
  defaultSize,
  className = '',
  allSizes = [],
  sizes = [],
  width,
  height,
  alt,
}) => {
  const currentSrcSet = generateSrcSet(image, allSizes);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (image && image.urls) {
      setLoadFailed(false); // Reset loadFailed state when image prop changes
    } else {
      setLoadFailed(true);
    }
  }, [image]);

  // Determine the src for the actual image
  let actualImageSrc: string | undefined = undefined;
  if (image && image.urls && image.urls['W' + defaultSize]) {
    actualImageSrc = image.urls['W' + defaultSize];
  }

  const showPlaceholder = loadFailed || !actualImageSrc;

  if (showPlaceholder) {
    return <ImagePlaceholder className={className} />;
  }

  return (
    <img
      className={className}
      src={actualImageSrc}
      srcSet={currentSrcSet.join()}
      sizes={sizes.join() || '100vw'}
      width={width}
      height={height}
      alt={alt || 'Image'}
      onError={() => {
        console.warn(`Failed to load image: ${actualImageSrc}`);
        setLoadFailed(true);
      }}
    />
  );
};

export default Image;
